import getBuildDataTotal from '@/state/selectors/saved-builds/get-build-data-total';
import store from '@/state/vanilla';
import EntryPointsHelper from '@/utils/entry-points-helper';
import NaasHelper from '@/utils/naas-helper';
import { NaasEvent } from '@/utils/naas-helper/enum';

class SsrPlaceholderHelper {
	private static _instance: SsrPlaceholderHelper;
	private timeout: ReturnType<typeof setTimeout> | null = null;
	private isCleanedUp: boolean = false;
	private suffix: string = '-ssr';
	private hasToCleanUp: boolean = Boolean(globalThis.dxNavSsrCanCleanUp);
	private proxyStore = { value: globalThis.dxNavLastClickedSelector };
	private proxy = new Proxy(this.proxyStore, {
		get(target, prop) {
			if (prop === 'value') return target[prop];
			return undefined;
		},
		set(target, prop, value) {
			if (prop === 'value') {
				target[prop] = value;
				return true;
			}
			return false;
		},
	});

	constructor() {
		this.cleanUp = this.cleanUp.bind(this);

		if (this.hasToCleanUp) {
			this.cleanUp();
		} else {
			this.listenYourBuildsChanges();
			this.listenToLastClickedProxyChanges();
		}
	}

	public static get instance(): SsrPlaceholderHelper {
		if (!this._instance) this._instance = new SsrPlaceholderHelper();
		return this._instance;
	}

	private listenToLastClickedProxyChanges() {
		this.proxy = new Proxy(this.proxyStore, {
			get: (target, prop) => {
				if (prop === 'value') return target[prop];
				return undefined;
			},
			set: (target, prop, value) => {
				if (prop === 'value') {
					target[prop] = value;
					this.cleanUp();
					return true;
				}
				return false;
			},
		});

		if (!globalThis.dxNavLastClickedSelector)
			globalThis.dxNavLastClickedSelector = '';

		Object.defineProperty(globalThis, 'dxNavLastClickedSelector', {
			get: () => this.proxy.value,
			set: (val) => {
				this.proxy.value = val;
			},
		});
	}

	private listenYourBuildsChanges() {
		const unsubscribe = store.subscribe((state) => {
			const total = getBuildDataTotal(state);
			if (total > 0) {
				this.cleanUp();
				unsubscribe();
			}
		});
	}

	public cleanUp(): void {
		if (this.timeout) clearTimeout(this.timeout);

		if (this.isCleanedUp) return;

		this.timeout = setTimeout(() => {
			const entryPoint = EntryPointsHelper.entryPoint;

			if (entryPoint?.hasChildNodes()) {
				entryPoint.style.opacity = '1';
				entryPoint.style.pointerEvents = 'auto';

				const navCopy = document.querySelector(
					`${EntryPointsHelper.entryPointSelector}${this.suffix}`,
				);
				navCopy?.remove();

				if (globalThis.dxNavLastClickedSelector !== undefined) {
					let dxNavLastClicked: HTMLAnchorElement | null = null;
					const isString =
						typeof globalThis.dxNavLastClickedSelector === 'string';
					const isNumeric =
						typeof globalThis.dxNavLastClickedSelector ===
							'number' &&
						!Number.isNaN(globalThis.dxNavLastClickedSelector);
					if (isString) {
						dxNavLastClicked =
							document.querySelector<HTMLAnchorElement>(
								`${(globalThis.dxNavLastClickedSelector as string).replace(this.suffix, '')} a`,
							);
					} else if (isNumeric) {
						const elements = Array.from(
							document.querySelectorAll<HTMLAnchorElement>(
								'#naas-headless-navigation-ssr a.dxnav__breadcrumb-link',
							),
						);
						dxNavLastClicked =
							elements[
								globalThis.dxNavLastClickedSelector as number
							];
					}
					dxNavLastClicked?.click();
				}

				this.isCleanedUp = true;
			}

			NaasHelper.dispatchEvent(NaasEvent.INIT);
			window.naasLoaded = true;
		}, 300);
	}
}

export default SsrPlaceholderHelper.instance;
